import { ChevronRight } from "@carbon/icons-react";
import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import type { ReactNode } from "react";
import React, { useEffect, useState } from "react";

export enum ExpanderPosition {
	Left,
	Right,
	None,
}

export interface ExpandedSectionProps {
	children: ReactNode;
	label: string | ReactNode;
	variant?: "default" | "negative";
	collapsedByDefault?: boolean;
	allowLabelClick?: boolean;
	expanderPosition?: ExpanderPosition;
	chevronClassName?: string;
	headerClassName?: string;
	contentClassName?: string;
	expanded?: boolean;
	onExpandedChange?: (expanded: boolean) => void;
}

export const ExpandedSection: React.FC<ExpandedSectionProps> = ({
	children,
	label,
	variant = "default",
	collapsedByDefault,
	allowLabelClick = true,
	expanderPosition = ExpanderPosition.Right,
	chevronClassName,
	headerClassName,
	contentClassName,
	expanded: propExpanded,
	onExpandedChange,
}) => {
	const [expanded, setExpanded] = useState(propExpanded || !collapsedByDefault);

	useEffect(() => {
		if (propExpanded !== undefined) {
			// Deliberately not using setExpandedAndNotify here to avoid triggering onExpandedChange
			setExpanded(propExpanded);
		}
	}, [propExpanded]);

	const setExpandedAndNotify = (expanded: boolean) => {
		setExpanded(expanded);
		onExpandedChange?.(expanded);
	};
	const handleOnClick = () => setExpandedAndNotify(!expanded);
	const handleOnKeyDown = (e) => {
		switch (e.key) {
			case "ArrowDown":
				setExpandedAndNotify(true);
				return;
			case "ArrowUp":
				setExpandedAndNotify(false);
				return;
			case "Enter":
			case " ":
				setExpandedAndNotify(!expanded);
				return;
			default:
				return;
		}
	};
	const chevronProps = {
		size: 16,
		className: cx("transition transition-all", {
			"fill-icon-default group-hover:fill-icon-hover active:fill-icon-active":
				variant !== "negative",
			"fill-icon-negative-default group-hover:fill-icon-negative-hover active:fill-icon-negative-active":
				variant === "negative",
		}),
	};

	const baseInteractivityProps = {
		onClick: handleOnClick,
		onKeyDown: handleOnKeyDown,
		tabIndex: 0,
		role: "button",
		["aria-label"]: "Toggle Section",
	};
	const minInteractivityProps = !allowLabelClick ? baseInteractivityProps : {};
	const maxInteractivityProps = allowLabelClick ? baseInteractivityProps : {};

	const expander = (
		<div
			{...minInteractivityProps}
			className={cx(
				chevronClassName,
				expanderPosition === ExpanderPosition.Right
					? "group rounded-full bg-button-normal-tertiary-default p-1 hover:bg-button-normal-tertiary-hover active:bg-button-normal-tertiary-active"
					: "group"
			)}
		>
			{expanded ? (
				<ChevronRight
					size={chevronProps.size}
					className={cx(
						chevronProps.className,
						expanderPosition === ExpanderPosition.Left
							? "rotate-90" // Expanded Left = Down
							: "-rotate-90" // Expanded Right = Up
					)}
				/>
			) : (
				<ChevronRight
					size={chevronProps.size}
					className={cx(
						chevronProps.className,
						expanderPosition === ExpanderPosition.Left
							? "rotate-0" // Collapsed Left = Right
							: "rotate-90" // Collapsed Right = Down
					)}
				/>
			)}
		</div>
	);

	return (
		<>
			{/* Header */}
			<div
				{...maxInteractivityProps}
				data-expanded={expanded}
				className={cx(
					"flex",
					"space-x-1",
					"focus-visible:outline",
					"outline-palette-brand-9",
					"-outline-offset-8",
					"outline-2",
					"items-center",
					headerClassName
				)}
			>
				{children !== "" && expanderPosition === ExpanderPosition.Left && expander}

				<div className="flex flex-1 items-center space-x-2">{label}</div>

				{children !== "" && expanderPosition === ExpanderPosition.Right && expander}
			</div>

			{/* Content */}
			{children && (
				<AnimatePresence initial={false}>
					{expanded && (
						<motion.div
							variants={{
								expanded: {
									opacity: 1,
									height: "auto",
									overflow: "visible",
									transition: {
										overflow: {
											delay: 0.3,
											duration: 0,
										},
									},
								},
								collapsed: {
									opacity: 0,
									height: 0,
									overflow: "hidden",
								},
							}}
							initial={"collapsed"}
							animate={"expanded"}
							exit={"collapsed"}
							transition={{
								duration: 0.3,
								ease: "easeIn",
							}}
							className={cx("flex flex-col", contentClassName)}
						>
							{children}
						</motion.div>
					)}
				</AnimatePresence>
			)}
		</>
	);
};
